/**
 * Serviço para gerenciar tokens de API do cliente
 */

import { getToken } from "./tokenService";

import { API_BASE_URL } from "@/lib/apiConfig";

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: string;
  };
  message?: string;
}

export interface ClientApiToken {
  id: string;
  client_id: string | null;
  token?: string;
  name?: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  revoked_at?: string;
}

export interface CreateTokenRequest {
  name?: string;
  description?: string;
}

/**
 * Lista todos os tokens de API de um cliente
 */
export async function listClientApiTokens(
  clientId: string
): Promise<ApiResponse<ClientApiToken[]>> {
  const token = getToken();
  if (!token) {
    throw new Error("Token não encontrado");
  }

  const response = await fetch(`${API_BASE_URL}/clients/${clientId}/api-tokens`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  return await response.json();
}

/**
 * Cria um novo token de API para o cliente
 */
export async function createClientApiToken(
  clientId: string,
  data: CreateTokenRequest
): Promise<ApiResponse<ClientApiToken>> {
  const token = getToken();
  if (!token) {
    throw new Error("Token não encontrado");
  }

  const response = await fetch(`${API_BASE_URL}/clients/${clientId}/api-tokens`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  return await response.json();
}

/**
 * Revoga um token de API do cliente
 */
export async function revokeClientApiToken(
  tokenId: string
): Promise<ApiResponse<ClientApiToken>> {
  const token = getToken();
  if (!token) {
    throw new Error("Token não encontrado");
  }

  const response = await fetch(
    `${API_BASE_URL}/clients/api-tokens/${tokenId}/revoke`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return await response.json();
}

/**
 * Renova um token de API (revoga o antigo e cria um novo)
 */
export async function renewClientApiToken(
  tokenId: string,
  data: CreateTokenRequest
): Promise<ApiResponse<ClientApiToken>> {
  const token = getToken();
  if (!token) {
    throw new Error("Token não encontrado");
  }

  const response = await fetch(
    `${API_BASE_URL}/clients/api-tokens/${tokenId}/renew`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    }
  );

  return await response.json();
}

